import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { clamp, drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const ROUND_SECONDS = 60;
const GROUND_Y = 600;
const PLAYER_X_HOME = 300;
const PLAYER_MIN_X = 176;
const PLAYER_MAX_X = 690;
const PLAYER_SIZE = 52;
const GRAVITY = 2600;
const JUMP_VELOCITY = -1020;
const BASE_SCROLL = 360;
const MAX_SCROLL = 640;
const HIT_INVULN = 0.85;

type EntityKind = "threat" | "token" | "leak";

interface Entity {
  kind: EntityKind;
  x: number;
  y: number;
  width: number;
  height: number;
  resolved: boolean;
}

export class PayloadRunnerGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0x9c17);
  private readonly entities: Entity[] = [];
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private distance = 0;
  private spawnGap = 420;
  private sinceSpawn = 260;
  private playerX = PLAYER_X_HOME;
  private playerY = GROUND_Y - PLAYER_SIZE;
  private velocityY = 0;
  private onGround = true;
  private integrity = 3;
  private invuln = 0;
  private shake = 0;
  private bob = 0;
  private message = "Carry the payload through the isolated lane";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {}

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.shake = Math.max(0, this.shake - deltaSeconds);
    this.invuln = Math.max(0, this.invuln - deltaSeconds);
    this.bob += deltaSeconds;
    if (this.messageTimer === 0) this.message = "Carry the payload through the isolated lane";

    if (this.time === 0) {
      this.finish("Isolated session delivered");
      return;
    }

    this.updatePlayer(deltaSeconds);
    this.updateLane(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    context.save();
    if (this.shake > 0 && !this.services.reducedMotion) {
      context.translate(Math.sin(this.elapsed * 96) * 6, Math.cos(this.elapsed * 71) * 3);
    }
    this.drawWorld(context);
    for (const entity of this.entities) this.drawEntity(context, entity);
    this.drawPlayer(context);
    context.restore();
    this.drawTelemetry(context);
    this.drawFooter(context);
    drawScanlines(context, "rgba(255, 255, 255, 0.02)");
  }

  getHud(): GameHud {
    return {
      score: Math.floor(this.score),
      time: this.time,
      message: this.message,
      ended: this.ended,
    };
  }

  destroy(): void {}

  private currentScrollSpeed(): number {
    return Math.min(MAX_SCROLL, BASE_SCROLL + this.elapsed * 4.6);
  }

  private updatePlayer(deltaSeconds: number): void {
    const input = this.services.input;
    const direction = Number(input.isDown("right")) - Number(input.isDown("left"));
    this.playerX = clamp(this.playerX + direction * 320 * deltaSeconds, PLAYER_MIN_X, PLAYER_MAX_X);

    const jumpRequested =
      input.consumePressed("primary") ||
      input.consumePressed("up") ||
      input.consumePressed("secondary");
    if (jumpRequested && this.onGround) {
      this.velocityY = JUMP_VELOCITY;
      this.onGround = false;
      this.services.audio.play("move");
    }

    this.velocityY += GRAVITY * deltaSeconds;
    this.playerY += this.velocityY * deltaSeconds;
    const floor = GROUND_Y - PLAYER_SIZE;
    if (this.playerY >= floor) {
      this.playerY = floor;
      this.velocityY = 0;
      this.onGround = true;
    }
  }

  private updateLane(deltaSeconds: number): void {
    const speed = this.currentScrollSpeed();
    const travel = speed * deltaSeconds;
    this.distance += travel;
    this.score += travel * 0.34;
    this.sinceSpawn += travel;

    if (this.sinceSpawn >= this.spawnGap) {
      this.sinceSpawn = 0;
      this.spawnGap = this.random.between(360, 560);
      this.spawnEntity();
    }

    for (let index = this.entities.length - 1; index >= 0; index -= 1) {
      const entity = this.entities[index];
      if (!entity) continue;
      entity.x -= travel;
      if (!entity.resolved && this.collides(entity)) this.resolveCollision(entity);
      if (entity.x + entity.width < -40) this.entities.splice(index, 1);
    }
  }

  private spawnEntity(): void {
    const roll = this.random.next();
    const spawnX = 1320;
    if (roll < 0.52) {
      const height = this.random.between(58, 128);
      this.entities.push({
        kind: "threat",
        x: spawnX,
        y: GROUND_Y - height,
        width: 46,
        height,
        resolved: false,
      });
    } else if (roll < 0.78) {
      const size = 34;
      const lift = this.random.between(70, 250);
      this.entities.push({
        kind: "token",
        x: spawnX,
        y: GROUND_Y - lift - size,
        width: size,
        height: size,
        resolved: false,
      });
    } else {
      const height = 44;
      this.entities.push({
        kind: "leak",
        x: spawnX,
        y: GROUND_Y - 232,
        width: 128,
        height,
        resolved: false,
      });
    }
  }

  private collides(entity: Entity): boolean {
    const px = this.playerX;
    const py = this.playerY;
    return (
      px < entity.x + entity.width &&
      px + PLAYER_SIZE > entity.x &&
      py < entity.y + entity.height &&
      py + PLAYER_SIZE > entity.y
    );
  }

  private resolveCollision(entity: Entity): void {
    if (entity.kind === "token") {
      entity.resolved = true;
      this.score += 150;
      this.services.audio.play("hit");
      this.setMessage("Clean payload byte secured", 1.4);
      return;
    }

    if (this.invuln > 0) return;
    entity.resolved = true;
    this.integrity -= 1;
    this.score = Math.max(0, this.score - 80);
    this.invuln = HIT_INVULN;
    this.shake = 0.4;
    this.services.audio.play("miss");
    const label = entity.kind === "leak" ? "Isolation leak" : "Malicious payload";
    if (this.integrity <= 0) {
      this.finish("Session breached");
      return;
    }
    this.setMessage(`${label} - ${this.integrity} isolation layers left`, 1.8);
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${Math.floor(this.score)}.`);
  }

  private drawWorld(context: CanvasRenderingContext2D): void {
    const sky = context.createLinearGradient(0, 0, 0, 720);
    sky.addColorStop(0, "#0a1230");
    sky.addColorStop(0.6, "#0c2440");
    sky.addColorStop(1, "#060b18");
    context.fillStyle = sky;
    context.fillRect(-20, -20, 1320, 760);

    // Isolation lane bounds
    context.strokeStyle = "rgba(139, 114, 255, 0.5)";
    context.setLineDash([14, 12]);
    context.lineWidth = 2;
    const laneTop = GROUND_Y - 300;
    context.beginPath();
    context.moveTo(0, laneTop);
    context.lineTo(1280, laneTop);
    context.stroke();
    context.setLineDash([]);

    // Parallax data streaks
    context.strokeStyle = "rgba(66, 226, 195, 0.14)";
    context.lineWidth = 2;
    const drift = this.services.reducedMotion ? 0 : this.distance;
    for (let index = 0; index < 12; index += 1) {
      const x = ((index * 150 - drift * 0.4) % 1360 + 1360) % 1360 - 40;
      const y = 120 + index * 21;
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(x + 60, y);
      context.stroke();
    }

    // Ground
    const ground = context.createLinearGradient(0, GROUND_Y, 0, 720);
    ground.addColorStop(0, "#13233c");
    ground.addColorStop(1, "#070d18");
    context.fillStyle = ground;
    context.fillRect(0, GROUND_Y, 1280, 720 - GROUND_Y);
    context.strokeStyle = "#2fe3ff";
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(0, GROUND_Y);
    context.lineTo(1280, GROUND_Y);
    context.stroke();

    // Ground grid motion
    context.strokeStyle = "rgba(47, 227, 255, 0.2)";
    context.lineWidth = 1;
    for (let index = 0; index < 22; index += 1) {
      const x = ((index * 72 - drift) % 1440 + 1440) % 1440 - 80;
      context.beginPath();
      context.moveTo(x, GROUND_Y);
      context.lineTo(x - 40, 720);
      context.stroke();
    }
  }

  private drawEntity(context: CanvasRenderingContext2D, entity: Entity): void {
    if (entity.resolved && entity.kind === "token") return;
    context.save();
    if (entity.kind === "threat") {
      context.shadowColor = "#ff5d5d";
      context.shadowBlur = 16;
      context.fillStyle = "rgba(40, 12, 20, 0.92)";
      context.strokeStyle = "#ff5d5d";
      context.lineWidth = 3;
      roundedRectPath(context, entity.x, entity.y, entity.width, entity.height, 8);
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      setText(context, 18, 900, "#ff9c9c", "center");
      context.fillText("</>", entity.x + entity.width / 2, entity.y + entity.height / 2);
    } else if (entity.kind === "leak") {
      context.shadowColor = "#ff7a1a";
      context.shadowBlur = 16;
      context.fillStyle = "rgba(44, 26, 8, 0.9)";
      context.strokeStyle = "#ff7a1a";
      context.lineWidth = 3;
      roundedRectPath(context, entity.x, entity.y, entity.width, entity.height, 8);
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      setText(context, 13, 900, "#ffca8a", "center");
      context.fillText("LEAK", entity.x + entity.width / 2, entity.y + entity.height / 2);
    } else {
      const pulse = this.services.reducedMotion ? 0 : Math.sin(this.bob * 6) * 3;
      context.shadowColor = "#42e2c3";
      context.shadowBlur = 18;
      context.fillStyle = "rgba(8, 34, 32, 0.92)";
      context.strokeStyle = "#42e2c3";
      context.lineWidth = 3;
      roundedRectPath(context, entity.x, entity.y + pulse, entity.width, entity.height, 9);
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      setText(context, 16, 900, "#9feddd", "center");
      context.fillText("{ }", entity.x + entity.width / 2, entity.y + entity.height / 2 + pulse);
    }
    context.restore();
  }

  private drawPlayer(context: CanvasRenderingContext2D): void {
    const cx = this.playerX + PLAYER_SIZE / 2;
    const cy = this.playerY + PLAYER_SIZE / 2;
    const flicker = this.invuln > 0 && Math.floor(this.invuln * 20) % 2 === 0;
    context.save();

    // Isolation bubble
    context.strokeStyle = flicker ? "rgba(139, 114, 255, 0.35)" : "rgba(139, 114, 255, 0.85)";
    context.lineWidth = 3;
    context.shadowColor = "#8b72ff";
    context.shadowBlur = 20;
    context.beginPath();
    context.arc(cx, cy, PLAYER_SIZE * 0.72, 0, Math.PI * 2);
    context.stroke();
    context.shadowBlur = 0;

    // Payload core
    context.fillStyle = flicker ? "#1b2740" : "#0b2238";
    context.strokeStyle = "#2fe3ff";
    context.lineWidth = 4;
    roundedRectPath(context, this.playerX + 8, this.playerY + 8, PLAYER_SIZE - 16, PLAYER_SIZE - 16, 10);
    context.fill();
    context.stroke();
    setText(context, 15, 900, "#9feddd", "center");
    context.fillText("PL", cx, cy);
    context.restore();
  }

  private drawTelemetry(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 42, 336, 92, "#8b72ff");
    setText(context, 12, 850, "#c8bcff");
    context.fillText("BROWSER ISOLATION LANE", 72, 72);
    setText(context, 24, 900, "#f7fbff");
    context.fillText(this.onGround ? "PAYLOAD RUNNING" : "AIRBORNE", 72, 106);

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("ISOLATION LAYERS", 1232, 62);
    for (let index = 0; index < 3; index += 1) {
      const x = 1128 + index * 40;
      context.strokeStyle = index < this.integrity ? "#8b72ff" : "#485362";
      context.lineWidth = 3;
      context.beginPath();
      context.arc(x, 96, 13, 0, Math.PI * 2);
      context.stroke();
    }
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 650, 1196, 54, "#8b72ff");
    setText(context, 20, 850, "#f7fbff");
    context.fillText(this.message, 72, 678);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("MOVE  \u2190 \u2192     JUMP  SPACE", 1204, 678);
  }
}
